import React, { useState } from "react";
import clsx from "clsx";
import type { ExtraType } from "../../../../../../../../common-deprecated/types/CommonTypes";
import Price from "../../../common/price/Price";
import SidePanel from "../../../../../../shared-components/css-modules/toyota/organisms/SidePanel";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import AddToBasket from "../../../common/AddToBasket";
import cardStyles from "../../../../../../shared-components/css-modules/toyota/molecules/Card.module.scss";
import styles from "./Extra.module.scss";
import { useCarConfigDispatch, useCarConfigSelector } from "../../../../../redux/store";
import { addExtra, removeExtra } from "../../../../../redux/slices/CarConfigBasketSlice";
import useAphExtra from "../../../../../hooks/useAphExtra";
import {
    trackOptionsBasketExtraAdd,
    trackOptionsBasketExtraRemove,
    trackOptionsExtrasLearnMore,
} from "../../../../../utils/tracking";
import { showAccessoriesRate } from "../../../../../utils/financeInsuranceRate";
import { getCarDBAssetSrcSet } from "../../../../../../../shared-logic/utils/imageUtils";
import { CarConfigLabel, useCarConfigLabel, useCarConfigOptionalLabel } from "../../../../../utils/labelUtils";
import { addNotification } from "../../../../../../../../common-deprecated/redux/actions/NotificationActions";

const Extra: React.FC<{ extra: ExtraType; firstShownExtra?: boolean }> = ({ extra, firstShownExtra }) => {
    const dispatch = useCarConfigDispatch();
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const addExtraNotificationLabel = useCarConfigLabel("carConfigA11yExtraAdded", { extra: extra.name });
    const removeExtraNotificationLabel = useCarConfigLabel("carConfigA11yExtraRemoved", { extra: extra.name });

    const [isSidePanelOpen, setIsSidePanelOpen] = useState<boolean>(false);

    const accessoryDisclaimerLabel = useCarConfigOptionalLabel("carConfigAccessoryDisclaimer");
    const { isSelected, availablePackName, includedPackName } = useAphExtra(extra);

    const handleClick = (): void => {
        if (isSelected) {
            trackOptionsBasketExtraRemove();
            dispatch(removeExtra({ extra }));
            dispatch(
                addNotification([
                    {
                        title: removeExtraNotificationLabel,
                        srOnly: true,
                    },
                ]),
            );
        } else {
            trackOptionsBasketExtraAdd();
            dispatch(addExtra({ extra, automatic: false }));
            dispatch(
                addNotification([
                    {
                        title: addExtraNotificationLabel,
                        srOnly: true,
                    },
                ]),
            );
        }
    };

    return (
        <>
            <article
                className={clsx(cardStyles.card, cardStyles.clickable, styles.wrapper, isSelected && cardStyles.active)}
                data-testid="extras-wrapper"
                onClick={handleClick}
            >
                {includedPackName && (
                    <div className={styles.banner}>
                        <Text variant="apheleia-body">
                            <CarConfigLabel
                                useHTML
                                label="carConfigExtrasIncludedInPack"
                                params={{ pack: includedPackName }}
                            />
                        </Text>
                    </div>
                )}
                {availablePackName && !includedPackName && (
                    <div className={styles.banner}>
                        <Text variant="apheleia-body">
                            <CarConfigLabel
                                useHTML
                                label="carConfigExtrasAvailableInPack"
                                params={{ pack: availablePackName }}
                            />
                        </Text>
                    </div>
                )}
                <img
                    {...getCarDBAssetSrcSet(
                        commonSettings,
                        [
                            { width: 172, height: 172 },
                            { width: 224, height: 224 },
                            { width: 344, height: 344 },
                            { width: 448, height: 448 },
                            { width: 894, height: 894 },
                        ],
                        extra.image,
                    )}
                    width="172"
                    height="172"
                    sizes="(max-width: 1199px) 100vw,(max-width: 1448px) 172px, 224px"
                    alt={extra.name}
                    loading="lazy"
                />
                <div className={styles.body}>
                    <Text variant="apheleia-h5" className={styles.title}>
                        {extra.name}
                    </Text>
                    <Button
                        variant="apheleia-text"
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            trackOptionsExtrasLearnMore();
                            setIsSidePanelOpen(true);
                        }}
                        className={styles.info}
                        autoFocus={firstShownExtra}
                    >
                        <CarConfigLabel label="carConfigExtrasLearnMore" />
                    </Button>
                    <div className={styles.price}>
                        <Text variant="apheleia-price">
                            <Price
                                price={extra.price}
                                forceCashPrice={!showAccessoriesRate(commonSettings, carConfigSettings)}
                            />
                        </Text>
                        <AddToBasket
                            itemName={extra.name}
                            active={isSelected}
                            className={clsx(cardStyles.indicator, styles.button)}
                            aria-pressed={isSelected}
                            data-testid="extra-add-to-basket-button"
                        />
                    </div>
                </div>
            </article>
            <SidePanel
                open={isSidePanelOpen}
                onRequestClose={() => {
                    setIsSidePanelOpen(false);
                }}
                title={extra.name}
            >
                <div className={styles["panel-item"]}>
                    <figure className={styles["panel-item-image"]}>
                        <img
                            {...getCarDBAssetSrcSet(
                                commonSettings,
                                [
                                    { width: 380, height: 380 },
                                    { width: 450, height: 450 },
                                    { width: 504, height: 504 },
                                    { width: 800, height: 800 },
                                    { width: 1000, height: 1000 },
                                ],
                                extra.image,
                            )}
                            width="380"
                            height="380"
                            sizes="(max-width: 1199px) 100vw, 50vw"
                            alt={extra.name}
                            loading="lazy"
                        />
                    </figure>
                    {extra.description && (
                        <Text
                            tag="span"
                            variant="apheleia-body"
                            spacing="spacing-0"
                            dangerouslySetInnerHTML={{ __html: extra.description }}
                        />
                    )}
                    {extra.type === "accessory" && !!accessoryDisclaimerLabel && <p>{accessoryDisclaimerLabel}</p>}
                </div>
            </SidePanel>
        </>
    );
};

export default Extra;
